import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { adminCmsAPI } from '../../lib/cmsApi';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Upload,
  Trash2,
  Loader2,
  Image,
  FileText,
  Copy,
  Check,
  FolderOpen,
  Grid,
  List,
} from 'lucide-react';

const CmsMedia = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [selectedFolder, setSelectedFolder] = useState('all');
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadFolder, setUploadFolder] = useState('general');
  const [uploadAltText, setUploadAltText] = useState('');

  const folders = ['all', 'general', 'services', 'formations', 'team', 'projects', 'pages'];

  useEffect(() => {
    if (user?.role !== 'administrateur' && user?.role !== 'employe') {
      navigate('/portail');
      return;
    }
    fetchMedia();
  }, [user, navigate]);

  const fetchMedia = async () => {
    try {
      const response = await adminCmsAPI.listMedia();
      setMedia(response.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des médias');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      for (const file of files) {
        await adminCmsAPI.uploadMedia(file, uploadFolder, uploadAltText);
      }
      toast.success(`${files.length} fichier(s) téléversé(s)`);
      setShowUploadDialog(false);
      setUploadAltText('');
      fetchMedia();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du téléversement');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce fichier?')) return;
    
    try {
      await adminCmsAPI.deleteMedia(id);
      toast.success('Fichier supprimé');
      fetchMedia();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const copyToClipboard = (url) => {
    const fullUrl = `${window.location.origin}${url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedUrl(url);
    toast.success('URL copiée');
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const filteredMedia = selectedFolder === 'all' 
    ? media 
    : media.filter(m => m.folder === selectedFolder);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#F9B438]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="cms-media">
      {/* Header */}
      <header className="bg-[#1D1D1B] text-white py-6">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/cms">
                <Button variant="ghost" size="icon" className="text-white/70 hover:text-white">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div>
                <h1 className="font-heading text-2xl font-bold">Médiathèque</h1>
                <p className="text-white/60 text-sm">{media.length} fichier(s)</p>
              </div>
            </div>
            <Button onClick={() => setShowUploadDialog(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Téléverser
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Filters */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            {folders.map((folder) => (
              <Button
                key={folder}
                variant={selectedFolder === folder ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedFolder(folder)}
              >
                {folder === 'all' ? 'Tous' : folder.charAt(0).toUpperCase() + folder.slice(1)}
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'outline'}
              size="icon"
              onClick={() => setViewMode('grid')}
            >
              <Grid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="icon"
              onClick={() => setViewMode('list')}
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Media Grid/List */}
        {filteredMedia.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Image className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
              <p className="text-neutral-500 mb-4">Aucun média dans ce dossier</p>
              <Button onClick={() => setShowUploadDialog(true)}>
                <Upload className="w-4 h-4 mr-2" />
                Téléverser des fichiers
              </Button>
            </CardContent>
          </Card>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {filteredMedia.map((item) => (
              <Card key={item.id} className="group overflow-hidden">
                <div className="aspect-square bg-neutral-100 relative">
                  {item.file_type === 'image' ? (
                    <img
                      src={item.url}
                      alt={item.alt_text || item.original_filename}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FileText className="w-12 h-12 text-neutral-300" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button
                      size="icon"
                      variant="secondary"
                      onClick={() => copyToClipboard(item.url)}
                    >
                      {copiedUrl === item.url ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      size="icon"
                      variant="destructive"
                      onClick={() => handleDelete(item.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <CardContent className="p-2">
                  <p className="text-xs text-neutral-600 truncate">{item.original_filename}</p>
                  <p className="text-xs text-neutral-400">{formatFileSize(item.file_size)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredMedia.map((item) => (
              <Card key={item.id}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-16 h-16 bg-neutral-100 rounded overflow-hidden flex-shrink-0">
                    {item.file_type === 'image' ? (
                      <img
                        src={item.url}
                        alt={item.alt_text || item.original_filename}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FileText className="w-8 h-8 text-neutral-300" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{item.original_filename}</p>
                    <p className="text-sm text-neutral-500">
                      {formatFileSize(item.file_size)} • {item.folder}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(item.url)}
                    >
                      {copiedUrl === item.url ? (
                        <Check className="w-4 h-4 mr-1" />
                      ) : (
                        <Copy className="w-4 h-4 mr-1" />
                      )}
                      Copier URL
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleDelete(item.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Téléverser des fichiers</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Dossier</Label>
              <select
                className="w-full h-10 px-3 rounded-md border border-input bg-background"
                value={uploadFolder}
                onChange={(e) => setUploadFolder(e.target.value)}
              >
                {folders.filter(f => f !== 'all').map((folder) => (
                  <option key={folder} value={folder}>
                    {folder.charAt(0).toUpperCase() + folder.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Texte alternatif (optionnel)</Label>
              <Input
                value={uploadAltText}
                onChange={(e) => setUploadAltText(e.target.value)}
                placeholder="Description de l'image..."
              />
            </div>
            <div className="space-y-2">
              <Label>Fichiers</Label>
              <div className="border-2 border-dashed rounded-lg p-8 text-center">
                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf"
                  onChange={handleUpload}
                  className="hidden"
                  id="file-upload"
                  disabled={uploading}
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  {uploading ? (
                    <Loader2 className="w-8 h-8 animate-spin text-[#F9B438] mx-auto mb-2" />
                  ) : (
                    <Upload className="w-8 h-8 text-neutral-400 mx-auto mb-2" />
                  )}
                  <p className="text-sm text-neutral-600">
                    {uploading ? 'Téléversement en cours...' : 'Cliquez pour sélectionner des fichiers'}
                  </p>
                  <p className="text-xs text-neutral-400 mt-1">
                    Images (JPG, PNG, WebP, GIF) et PDF
                  </p>
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CmsMedia;
